import { iCity } from "core/kangarooTypes/iCity";
import AreaFormDialog from "./AreaFormDialog";
import { iArea } from "core/kangarooTypes/iArea";
import { useEffect, useState } from "react";
import Button from "shared/Button";
import { Pencil, Plus, Trash2, Loader } from "lucide-react";
import { getCites, getAreas } from "core/Services/CommonServices";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { addArea, updateArea, deleteArea } from "core/Services/AdminServices";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

// Skeleton component for loading state
const AreaTableSkeleton = () => {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
              {t("areaManagement.areaId")}
            </th>
            <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
              {t("areaManagement.areaName")}
            </th>
            <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
              {t("areaManagement.deliveryFee")}
            </th>
            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">
              {t("areaManagement.actions")}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {[...Array(5)].map((_, index) => (
            <tr key={index} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <Skeleton width={40} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <Skeleton width={150} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <Skeleton width={80} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right">
                <div className="flex justify-end gap-2">
                  <Skeleton width={20} height={20} circle />
                  <Skeleton width={20} height={20} circle />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AreaManagement = () => {
  const { t } = useTranslation();
  const [cities, setCities] = useState<iCity[]>();
  const [areas, setAreas] = useState<iArea[]>();
  const [selectedCityId, setSelectedCityId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingArea, setEditingArea] = useState<iArea | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [deletingAreaId, setDeletingAreaId] = useState<number | null>(null);

  const fetchData = async (cityId?: number) => {
    try {
      setIsLoading(true);
      const [citiesResponse, areasResponse] = await Promise.all([
        getCites(),
        getAreas(cityId),
      ]);
      setCities(citiesResponse.data);
      setAreas(areasResponse.data);
    } catch (error) {
      toast.error(t("areaManagement.failedToFetch"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const cityId = event.target.value ? parseInt(event.target.value) : null;
    setSelectedCityId(cityId);
    fetchData(cityId || undefined);
  };

  const handleAddArea = () => {
    setEditingArea(null);
    setShowModal(true);
  };

  const handleEditArea = (area: iArea) => {
    setEditingArea(area);
    setShowModal(true);
  };

  const handleDeleteArea = async (id: number) => {
    try {
      setDeletingAreaId(id);
      await deleteArea(id);
      fetchData();
      toast.success(t("areaManagement.areaDeleted"));
    } catch (error) {
      toast.error(t("areaManagement.failedToDelete"));
    } finally {
      setDeletingAreaId(null);
    }
  };

  // Define the form data type to match the validation schema
  type AreaFormData = {
    id?: number;
    cityId: number;
    name: string;
    price: number;
  };

  const handleSubmit = async (data: AreaFormData) => {
    try {
      if (editingArea) {
        await updateArea(data, editingArea.id);
        toast.success(t("areaManagement.areaUpdated"));
      } else {
        await addArea(data);
        toast.success(t("areaManagement.areaAdded"));
      }
      fetchData();
      setShowModal(false);
    } catch (error) {
      toast.error(t("areaManagement.failedToSave"));
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div
        className="flex flex-col gap-2 md:flex-row md:justify-between 
      mb-6 w-full"
      >
        <h2 className="text-xl font-medium text-gray-900">{t("areaManagement.title")}</h2>
        <div className="flex gap-4">
          <select
            className="border border-gray-300 rounded-md p-2 "
            value={selectedCityId || ""}
            onChange={handleCityChange}
          >
            <option value="">{t("areaManagement.allCities")}</option>
            {cities?.map((city) => (
              <option key={city.id} value={city.id}>
                {city.name}
              </option>
            ))}
          </select>
          <Button
            onClick={handleAddArea}
            icon={<Plus size={16} />}
            disabled={cities?.length === 0}
          >
            {t("areaManagement.addNewArea")}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <AreaTableSkeleton />
      ) : cities?.length === 0 ? (
        <div className="p-4 border border-yellow-300 bg-yellow-50 rounded-md text-yellow-800">
          {t("areaManagement.noCitiesMessage")}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                  {t("areaManagement.areaId")}
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                  {t("areaManagement.areaName")}
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                  {t("areaManagement.deliveryFee")}
                </th>
                <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">
                  {t("areaManagement.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {areas?.map((area) => (
                <tr key={area.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {area.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {area.name}
                  </td>{" "}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    JOD {area.price.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        className="text-blue-600 hover:text-blue-900"
                        onClick={() => handleEditArea(area)}
                      >
                        <Pencil className="h-5 w-5" />
                      </button>
                      <button
                        className={`text-red-600 hover:text-red-900 ${
                          deletingAreaId === area.id
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                        onClick={() => handleDeleteArea(area.id)}
                        disabled={deletingAreaId === area.id}
                      >
                        {deletingAreaId === area.id ? (
                          <Loader className="h-5 w-5 animate-spin" />
                        ) : (
                          <Trash2 className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Area Form Dialog */}
      <AreaFormDialog
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmit}
        initialData={editingArea || undefined}
        isEditing={!!editingArea}
        cities={cities || []}
      />
    </div>
  );
};

export default AreaManagement;
